import { useState, useEffect } from 'react';
import { X, Mail, Trash2 } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { supabase } from '../../lib/supabaseClient';
import toast from 'react-hot-toast';

interface GmailSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
}

interface GmailConnection {
  id: string;
  email: string;
  created_at: string;
  business_id: string;
}

export default function GmailSettings({ isOpen, onClose, onSave }: GmailSettingsProps) {
  const { selectedBusiness } = useBusiness();
  const [gmailConnections, setGmailConnections] = useState<GmailConnection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisconnecting, setIsDisconnecting] = useState<string | null>(null);

  // Load Gmail connections
  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      loadGmailConnections();
    }
  }, [isOpen, selectedBusiness?.id]);

  const loadGmailConnections = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('gmail_connections')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });

      if (!error && data) {
        setGmailConnections(data);
      }
    } catch (error) {
      console.error('Error loading Gmail connections:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnectGmail = async (connectionId: string) => {
    if (!selectedBusiness?.id) return;
    
    setIsDisconnecting(connectionId);
    try {
      const { error } = await supabase
        .from('gmail_connections')
        .delete()
        .eq('id', connectionId)
        .eq('business_id', selectedBusiness.id);

      if (error) throw error;

      // Update local state
      setGmailConnections(prev => prev.filter(conn => conn.id !== connectionId));
      
      toast.success('Gmail account disconnected successfully');
      onSave?.();
      
    } catch (error) {
      console.error("Error disconnecting Gmail:", error);
      toast.error('Failed to disconnect Gmail account');
    } finally {
      setIsDisconnecting(null);
    }
  };

  const handleConnectGmail = async () => {
    try {
      // Use Vite environment variables
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      // Build redirect URI: https://[project-ref].supabase.co/functions/v1/gmail-oauth-callback
      const supabaseUrlClean = supabaseUrl.replace('/rest/v1', '').replace(/\/$/, '');
      const redirectUrl = `${supabaseUrlClean}/functions/v1/gmail-oauth-callback`;
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      
      console.log('🔗 Gmail OAuth Redirect URI:', redirectUrl);
      console.log('⚠️  This URI must EXACTLY match what is registered in Google Cloud Console!');
      
      if (!clientId) {
        toast.error("Google Client ID not configured. Please contact support.");
        return;
      }

      const scope = 'https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/userinfo.email';
      
      // Encode state as base64 JSON to match what callback expects
      const stateData = {
        businessId: selectedBusiness?.id,
        returnUrl: window.location.href
      };
      const encodedState = btoa(JSON.stringify(stateData));
      
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUrl)}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=consent&state=${encodeURIComponent(encodedState)}`;
      
      window.open(authUrl, '_blank', 'width=500,height=600');
      
      // Listen for OAuth completion
      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === 'GMAIL_OAUTH_SUCCESS') {
          window.removeEventListener('message', handleMessage);
          loadGmailConnections(); // Reload Gmail connections
          toast.success('Gmail connected successfully!');
          onSave?.();
        } else if (event.data.type === 'GMAIL_OAUTH_ERROR') {
          window.removeEventListener('message', handleMessage);
          toast.error('Failed to connect Gmail');
        }
      };
      
      window.addEventListener('message', handleMessage);
      
    } catch (error) {
      console.error('Error connecting Gmail:', error);
      toast.error('Failed to connect Gmail');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10001] flex items-center justify-center p-4" style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}>
      <div className="bg-gray-900 rounded-3xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col pointer-events-auto">
        {/* Header */}
        <div className="bg-gray-900 border-b border-gray-800 px-8 py-6 rounded-t-3xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center">
                <Mail className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Gmail Integration</h2>
                <p className="text-sm text-gray-400 mt-1">Manage your Gmail connections</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Connected Accounts */}
              {gmailConnections.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-white mb-4">Connected Gmail Accounts</h3>
                  <div className="space-y-3">
                    {gmailConnections.map((connection) => (
                      <div key={connection.id} className="p-4 bg-gray-800 border border-gray-700 rounded-xl">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
                              <Mail className="h-4 w-4 text-white" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-white">{connection.email}</p>
                              <p className="text-xs text-gray-400">
                                Connected {new Date(connection.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDisconnectGmail(connection.id)}
                            disabled={isDisconnecting === connection.id}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                            title="Disconnect Gmail account"
                          >
                            {isDisconnecting === connection.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-400"></div>
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Connect New Account */}
              <div>
                <h3 className="text-sm font-medium text-white mb-4">
                  {gmailConnections.length > 0 ? 'Connect Additional Account' : 'Connect Gmail Account'}
                </h3>
                <div className="p-6 bg-gray-800 border border-gray-700 rounded-xl text-center">
                  <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="text-lg font-medium text-white mb-2">Connect Gmail</h4>
                  <p className="text-sm text-gray-400 mb-6">
                    Connect your Gmail account to enable email automation and communication features.
                  </p>
                  <button
                    onClick={handleConnectGmail}
                    className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-2xl transition-colors font-medium"
                  >
                    Connect Gmail Account
                  </button>
                </div>
              </div>

              {/* Information */}
              <div className="p-4 bg-gray-800/50 border border-gray-700/50 rounded-xl">
                <h4 className="text-sm font-medium text-white mb-2">What can you do with Gmail integration?</h4>
                <ul className="text-xs text-gray-400 space-y-1">
                  <li>• Send automated emails to customers</li>
                  <li>• Email proposals and invoices directly</li>
                  <li>• Receive notifications and updates</li>
                  <li>• Integrate with your existing email workflow</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-800">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}